import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, Users, TrendingUp } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSocialProofEnabled } from "@/hooks/useSocialProofEnabled";

const CurrentlyViewing = ({ 
  treatmentName = "", 
  variant = "badge",
  className = "" 
}) => {
  const { language } = useLanguage();
  const enabled = useSocialProofEnabled();
  const [viewerCount, setViewerCount] = useState(0);
  const [recentBookings, setRecentBookings] = useState(0);

  useEffect(() => {
    // Generate initial count with wide variance
    const hour = new Date().getHours();
    const day = new Date().getDay();
    const isWeekend = day === 0 || day === 6;
    const isPeakHours = hour >= 10 && hour <= 14;
    const isEvening = hour >= 18 && hour <= 21;
    const isLateNight = hour >= 23 || hour <= 6;

    let base;
    if (isLateNight) base = 3 + Math.floor(Math.random() * 6); // 3-8
    else if (isPeakHours) base = 28 + Math.floor(Math.random() * 19); // 28-46
    else if (isEvening) base = 18 + Math.floor(Math.random() * 15); // 18-32
    else if (isWeekend) base = 12 + Math.floor(Math.random() * 22); // 12-33
    else base = 9 + Math.floor(Math.random() * 18); // 9-26

    setViewerCount(base);
    setRecentBookings(Math.floor(Math.random() * 12) + 2);

    // Fluctuate with varied intervals and varied step sizes
    const tick = () => {
      setViewerCount(prev => {
        // Occasionally have bigger swings
        const bigSwing = Math.random() < 0.15;
        const maxChange = bigSwing ? 7 : 3;
        const change = Math.floor(Math.random() * (maxChange * 2 + 1)) - maxChange;
        const next = prev + change;
        return Math.max(2, Math.min(52, next));
      });

      // Vary the next interval between 8-45 seconds
      const nextDelay = 8000 + Math.random() * 37000;
      timeoutRef = setTimeout(tick, nextDelay);
    };

    let timeoutRef = setTimeout(tick, 10000 + Math.random() * 15000);
    return () => clearTimeout(timeoutRef);
  }, []);

  // Honor the admin "Social Proof Widget" automation toggle. When OFF
  // (default), all three variants of this component render nothing —
  // matching the behavior of FloatingTrending / FloatingSocialProof.
  if (enabled !== true) return null;

  const t = {
    en: {
      viewing: "people viewing",
      viewingThis: "people viewing this",
      recentlyBooked: "booked in last 24h",
      popular: "Popular right now",
      highDemand: "High demand"
    },
    fr: {
      viewing: "personnes consultent",
      viewingThis: "personnes consultent ceci",
      recentlyBooked: "reserve dans les dernieres 24h",
      popular: "Populaire en ce moment",
      highDemand: "Forte demande"
    }
  };

  const text = t[language] || t.en;

  if (variant === "badge") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`inline-flex items-center gap-1.5 bg-green-50 text-green-700 text-xs px-2.5 py-1 rounded-full ${className}`}
        data-testid="currently-viewing-badge"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
        <span className="font-medium">{viewerCount}</span>
        <span>{text.viewing}</span>
      </motion.div>
    );
  }

  if (variant === "inline") {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`flex items-center gap-2 text-xs text-muted-foreground ${className}`}
        data-testid="currently-viewing-inline"
      >
        <Eye className="w-3 h-3" />
        <span>{viewerCount} {text.viewingThis}</span>
        {recentBookings > 5 && (
          <span className="text-amber-600 font-medium ml-1">• {text.highDemand}</span>
        )}
      </motion.div>
    );
  }

  if (variant === "card") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 ${className}`}
        data-testid="currently-viewing-card"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="font-semibold text-green-800">{text.popular}</span>
              </div>
              <p className="text-sm text-green-600">
                <span className="font-bold">{viewerCount}</span> {text.viewing}
                {treatmentName && ` ${treatmentName}`}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-amber-600">
              <TrendingUp className="w-4 h-4" />
              <span className="font-semibold">{recentBookings}</span>
            </div>
            <p className="text-xs text-muted-foreground">{text.recentlyBooked}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
};

export default CurrentlyViewing;
